import React, { useCallback, useEffect, useState } from 'react'

import { Table } from '@shared/components/table/table.component'
import { TableActionLink } from '@shared/components/table/table-action-link.component'
import { LocationDecorated, Roles } from '@shared/types'

import { AuditModes } from './select-location.types'
import ids from '@shared/onboarding/ids'
import Joyride from 'react-joyride'
import { onboardingConfig } from '@shared/onboarding/config'
import {
  selectLocationPageStepsAdmin,
  selectLocationPageStepsClient,
} from '@shared/onboarding/steps'
import {
  OnboardingType,
  useOnboardingStore,
} from '@shared/onboarding/onboarding.store'
import { ACL } from '@shared/components/acl.component'
import { useACL } from '@shared/hooks/use-acl.hook'

export enum CompanyActions {
  PREVIEW = 'Zobacz',
  CONTINUE_AUDIT = 'Kontynuuj audyt',
  CONFIGURE_LOCATION = 'Konfiguruj lokalizację',
  RUN_AUDIT = 'Uruchom audyt',
  ENTER = 'Wejdź',
}

type Props = {
  isLoading: boolean
  companies: LocationDecorated[]
  mode: AuditModes
}

export function SelectLocationTable(props: Props) {
  const { isActive, setActive } = useOnboardingStore()
  const { is } = useACL()
  const isClientBasic = is([Roles.ClientBasic])

  const getUrlByAction = useCallback(
    (company: LocationDecorated, action: CompanyActions) => {
      switch (action) {
        case CompanyActions.CONTINUE_AUDIT:
          return `/company-profile/${company.companyID}/${company.ongoingAuditId}`
        case CompanyActions.PREVIEW:
          return `/company-profile/${company.companyID}`
        case CompanyActions.CONFIGURE_LOCATION:
          return `/company-profile/${company.companyID}/config`
        case CompanyActions.RUN_AUDIT:
          return `/company-profile/${company.companyID}/run`
        default:
          return '/'
      }
    },
    []
  )

  useEffect(() => {
    if (isActive(OnboardingType.SELECT_LOCATION_PAGE)) {
      setActive(OnboardingType.SELECT_LOCATION_PAGE, false)

      const timer = setTimeout(() => {
        setActive(OnboardingType.SELECT_LOCATION_PAGE, true)
      }, 1000)

      return () => clearTimeout(timer)
    }
  }, [])

  return (
    <>
      <ACL
        allowedRoles={[
          Roles.Client,
          Roles.ClientEditor,
          Roles.ClientAdministrator,
          Roles.ClientBasic,
        ]}
      >
        <Joyride
          disableScrolling={true}
          {...onboardingConfig}
          steps={
            isClientBasic
              ? selectLocationPageStepsClient
              : selectLocationPageStepsAdmin
          }
          continuous
          run={isActive(OnboardingType.SELECT_LOCATION_PAGE)}
          callback={(data) => {
            if (data.status === 'finished' || data.status === 'skipped') {
              setActive(OnboardingType.SELECT_LOCATION_PAGE, false)
            }
          }}
        />
      </ACL>
      <Table<LocationDecorated>
        isLoading={props.isLoading}
        title="Wybierz lokalizację"
        columns={[
          {
            title: 'Lokalizacja',
            field: 'name',
          },
          {
            title: 'Adres',
            field: 'companyHeadquaters',
          },
          {
            title: 'Akcje',
            render: (company: LocationDecorated) => {
              const actions = Array.isArray(company.action)
                ? company.action
                : [company.action]

              const isFirstRow = company.tableData?.index === 0

              return (
                <div style={{ display: 'flex', gap: '8px' }}>
                  {actions.map((action) => (
                    <div
                      key={action}
                      id={isFirstRow ? ids.SELECT_LOCATION_OPEN : undefined}
                    >
                      <TableActionLink
                        to={getUrlByAction(company, action as CompanyActions)}
                      >
                        {action}
                      </TableActionLink>
                    </div>
                  ))}
                </div>
              )
            },
          },
        ].filter(Boolean)}
        data={props.companies}
      />
    </>
  )
}
